import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPhone, FiPhoneCall, FiCreditCard, FiUsers, FiMessageCircle, FiShield, FiChevronRight, FiTarget } = FiIcons;

const Products = () => {
  const navigate = useNavigate();

  const products = [
    {
      name: "Donna",
      title: "Your Inbound Appointment Setter",
      description: "Donna answers patient calls, books appointments instantly, and never misses a call. She also sends messages to the practice in real time on a HIPAA compliant platform so staff know who to call back and exactly what the patient needs. She's your front desk hero, ensuring more patients get through your doors.",
      icon: FiPhone,
      color: "from-dental-blue to-ai-teal",
      featured: true
    },
    {
      name: "Marketing Lead AI",
      title: "Lead-to-Appointment Optimizer",
      description: "Converts marketing leads into booked consultations by intelligently qualifying prospects and scheduling meetings. Reduces cost per acquisition while maximizing your marketing ROI.",
      icon: FiTarget,
      color: "from-energy-coral to-growth-green"
    },
    {
      name: "Outbound AI Agent",
      title: "Follow-up & Reminder Specialist",
      description: "Follows up with leads, reminds patients of appointments, and even checks in post-op.",
      icon: FiPhoneCall,
      color: "from-ai-teal to-growth-green"
    },
    {
      name: "Patient Financing AI",
      title: "Real-time Loan Pre-approvals",
      description: "Provides real-time loan pre-approvals before patients arrive, helping dentists recommend the right treatments without worrying about payment.",
      icon: FiCreditCard,
      color: "from-growth-green to-energy-coral"
    },
    {
      name: "Database Reactivation AI",
      title: "Bring Back Lost Patients",
      description: "Brings lapsed patients back into the chair with smart outbound campaigns.",
      icon: FiUsers,
      color: "from-energy-coral to-dental-blue"
    },
    {
      name: "Pre-Op FAQ Chatbot",
      title: "24/7 Patient Support",
      description: "Answers common patient questions before treatment, freeing staff to focus on care.",
      icon: FiMessageCircle,
      color: "from-dental-blue to-ai-teal"
    },
    {
      name: "Insurance Verification AI",
      title: "Seamless Verification",
      description: "Handles verification seamlessly for both inbound and outbound calls.",
      icon: FiShield,
      color: "from-ai-teal to-growth-green"
    }
  ];

  const handleNavigateToProducts = () => {
    navigate('/products');
    // Scroll to top after navigation
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="products" className="py-24 bg-gradient-to-b from-clean-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-slate-gray mb-6">
            Meet{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-dental-blue to-ai-teal">
              Donna
            </span>{' '}
            and Her Friends
          </h2>
          <p className="text-xl text-slate-gray opacity-80 max-w-4xl mx-auto leading-relaxed">
            A suite of AI solutions designed to handle the tasks that slow your practice down. Whether it's booking new patients, 
            optimizing marketing leads, verifying insurance, or guiding patients through financing, our AI team works 24/7 to grow your bottom line.
          </p>
        </motion.div>

        {/* Featured Product - Donna */}
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="bg-gradient-to-br from-dental-blue to-ai-teal rounded-3xl p-8 md:p-12 text-clean-white shadow-2xl">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-clean-white bg-opacity-20 rounded-2xl flex items-center justify-center">
                    <SafeIcon icon={FiPhone} className="text-3xl text-clean-white" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold">Donna</h3>
                    <p className="text-clean-white opacity-90">Flagship Product</p>
                  </div>
                </div>
                <h4 className="text-2xl font-semibold mb-4">Your Inbound Appointment Setter</h4>
                <p className="text-lg leading-relaxed opacity-90">
                  Donna answers patient calls, books appointments instantly, and never misses a call. 
                  She also sends messages to the practice in real time on a HIPAA compliant platform so staff know who to call back and exactly what the patient needs. 
                  She's your front desk hero, ensuring more patients get through your doors.
                </p>
              </div>
              <div className="flex justify-center">
                <motion.div
                  className="relative"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  <div className="w-48 h-48 bg-gradient-to-br from-clean-white to-growth-green rounded-full flex items-center justify-center shadow-2xl">
                    <SafeIcon icon={FiPhone} className="text-dental-blue text-6xl" />
                  </div>
                  {/* Pulse rings for Donna */}
                  {[...Array(2)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute inset-0 rounded-full border-2 border-clean-white"
                      animate={{
                        scale: [1, 1.3, 1.6],
                        opacity: [0.6, 0.3, 0],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        delay: i * 1.5,
                      }}
                    />
                  ))}
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Other Products Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.slice(1).map((product, index) => (
            <motion.div
              key={product.name}
              className="bg-clean-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <div className={`w-16 h-16 bg-gradient-to-br ${product.color} rounded-2xl flex items-center justify-center mb-6`}>
                <SafeIcon icon={product.icon} className="text-2xl text-clean-white" />
              </div>
              
              <h3 className="text-xl font-bold text-slate-gray mb-2">{product.name}</h3>
              <h4 className="text-lg font-semibold text-dental-blue mb-4">{product.title}</h4>
              <p className="text-slate-gray opacity-80 leading-relaxed">{product.description}</p>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <motion.button
            onClick={handleNavigateToProducts}
            className="bg-gradient-to-r from-dental-blue to-ai-teal text-clean-white px-8 py-4 rounded-xl font-semibold text-lg flex items-center space-x-3 mx-auto hover:shadow-lg transition-all duration-200"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span>See Which AI Fits Your Practice</span>
            <SafeIcon icon={FiChevronRight} className="text-xl" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Products;